import React from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import {action, computed, observable, reaction} from 'mobx';
import {observer, inject} from 'mobx-react';
import {withRouter, routerShape} from 'react-router';
import autobind from 'autobind-decorator';

import isValidException from 'utils/isValidException';
import {withConfig, withI18n} from 'spa/Components/Context';
import {browserOnly} from 'spa/Components/EnvSplit';

import GiftsPaymentSection from './GiftsPaymentSection';

import {PAYMENT_TYPES} from '../../state/payments/billing';

import './GiftsCheckout.less';
import GiftsRedeemSession from './GiftsRedeemSession';

@browserOnly
@withRouter
@inject(({
  store:
  {
    billing,
    cities,
    gifts,
    i18n,
    lookupEmail,
    notifications,
    products,
    session,
    tracker,
    vouchers,
  },
}) => (
  {
    billing,
    cities,
    gifts,
    i18n,
    loadedCities: cities.getSearch({testKey: 'allCities', visible: '1'}),
    lookupEmail,
    notifications,
    pricing: products.getSearch({type: '*', city: gifts.orderContent && gifts.orderContent.city_id}),
    products,
    tracker,
    session,
    vouchers,
  }
))
@observer
@withI18n
@withConfig
class GiftsCheckout extends React.Component {
  static propTypes = {
    billing: PropTypes.object.isRequired,
    gifts: PropTypes.object.isRequired,
    giftUpgrade: PropTypes.object.isRequired,
    notifications: PropTypes.object.isRequired,
    pricing: PropTypes.object,
    products: PropTypes.object,
    router: routerShape.isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }),
    session: PropTypes.object.isRequired,
    setShowFooter: PropTypes.func.isRequired,
    vouchers: PropTypes.object.isRequired,
  };

  UNSAFE_componentWillMount() {
    const {config, i18n} = this.context;
    const {
      billing: {
        resetChargeData,
        updateChargeCity,
        updateChargeCountry,
      },
      gifts,
    } = this.props;

    const lastCityId = (gifts.orderContent && gifts.orderContent.city_id) || '';

    resetChargeData();

    updateChargeCity(lastCityId);
    updateChargeCountry(i18n.umRegion);

    this.orderContent = gifts.orderContent;

    this.infoPriceEnvelopeActive = config.getValue('locale.b2c.gifts-envelope-info-price-active', i18n.umRegion);

    this.envelopeImage = config.getValue('locale.b2c.gifts-envelope-image-default', i18n.umRegion);

    this.envelopeImagesSpecialOccasion = config.getValue('locale.b2c.gifts-envelope-image-special-occasion', i18n.umRegion);
  }

  componentDidMount() {
    const {
      billing,
      gifts,
      router,
    } = this.props;
    const {orderContent} = this;
    const { updateCharge } = billing;
    const {i18n} = this.context;

    if (gifts.orderContent && !gifts.orderContent.amount) {
      router.push(`/${i18n.lc}/buy-gift-card/`);
      return;
    }

    this.listeners = [
      reaction(
        () => orderContent && orderContent.amount,
        (amount) => {
          updateCharge(amount);
        },
        {
          name: 'Update charge value in billing state',
          fireImmediately: true,
        },
      ),
      reaction(
        () => this.props.session.user,
        (user) => {
          if (user) {
            this.sessionSuccess();
          }
        },
        {
          name: 'GiftsCheckout - onSessionSuccess',
          fireImmediately: true,
        },
      ),
    ];
  }

  componentWillUnmount() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @observable giftDisplayText = '';
  @observable processingForm = false;
  @observable orderContent = {};

  /*
   * Setup our observables for our payment information
   */
  @observable paymentInfo = {
    /* card, paypal or applepay */
    paymentOption: null,
    paymentToken: null,
  };

  /*
   * Handles our apple pay clicks - all ApplePaySession instantiations etc MUST happen within a user
   * initiated action such as an onClick. We trigger a hidden submit button once we've setup our
   * callbacks up so that we can use the native html5 validation for our form. Without which the
   * user would be uncertain as to why they cannot press the apple pay button.
   */
  @autobind
  @action('onApplePayClick - GiftsCheckout')
  onApplePayClick(event) {
    event.preventDefault();
    const {i18n} = this.context;
    const {billing} = this.props;

    const {currencyData} = this;
    if (!currencyData) return null;

    const paymentRequest = billing.createPaymentRequest({
      currency_id: currencyData.id,
      total: {
        label: 'Urban Massage',
        amount: this.orderContent.amount,
      },
    });

    /**
     * instantiate our ApplePaySession
     * @see https://developer.apple.com/documentation/applepayjs/applepaysession
     */
    this.session = new ApplePaySession(1, paymentRequest);

    // Setup our onvalidatemerchant callback
    this.session.onvalidatemerchant = (evt) => {
      // pass the event to our validate method
      billing.validateApplePay(evt)
        .then((merchantSession) => {
          // complete validation
          this.session.completeMerchantValidation(merchantSession);
        })
        .then(
          null,
          action('Apple Pay validation fail', (validationErr) => {
            // Handle our validation errors by showing a notification to the user
            console.error('Error validating merchant:', validationErr);
            this.props.notifications.add({
              id: 'appleValidation',
              type: 'error',
              title: i18n._x('There was a problem validating Apple Pay. Please try again', 'Apple Pay validation error'),
            });

            // Abort our session;
            return this.session.abort();
          }),
        );
    };

    // Setup our payment authorised callback
    this.session.onpaymentauthorized = (evt) => {
      // Get payment data to be submitted
      billing.getApplePayPaymentData(evt)
        .then((payload) => {
          /*
           * Pass a success status to our session / apple
           * @see https://developer.apple.com/documentation/applepayjs/applepaysession/apple_pay_status_codes
           */
          this.session.completePayment(ApplePaySession.STATUS_SUCCESS);

          this.orderContent.paymentData = payload;

          this.completePurchase();
        })
        .catch((error) => {
          if (!i18n.isKnownError(error)) {
            // This is unexpected so record it in Sentry
            if (isValidException(error)) {
              Sentry.captureException(error);
            } else {
              Sentry.captureMessage('Gifts Checkout get ApplePay payment data failure');
            }
          }

          // Alert our user to the apple pay error
          this.props.notifications.add({
            id: 'Apple payment data error',
            type: 'error',
            title: i18n._x('There was a problem validating Apple Pay. Please try again', 'Apple Pay validation error'),
          });
          // Send a failure status code to Apple Pay
          this.session.completePayment(ApplePaySession.STATUS_FAILURE);
        });
    };

    // Once our handlers are set then we click our hidden submit to check for validation issues.
    document.getElementById('js-apple-submit').click();
  }

  /*
   *  Complete the voucher purchase process
   */
  @action('completePurchase - GiftsCheckout')
  completePurchase() {
    const {i18n} = this.context;
    const {notifications, router, tracker} = this.props;

    this.props.vouchers.voucherCreate.load({}, this.orderContent).then((response) => {
      const voucherId = response && response.voucher && response.voucher.id;

      const {gifts} = this.props;

      tracker.track('Success payment on gifts checkout page', {
        category: 'Gifts Checkout',
        label: voucherId,
        city: gifts && gifts.orderContent && gifts.orderContent.city_id,
      });

      if (this.props.giftUpgrade && this.props.giftUpgrade.upgrade_value) {
        tracker.track('Purchased Upsell With Gift', {
          category: 'Gifts Checkout',
          voucherId,
          upsell_value: this.props.giftUpgrade && this.props.giftUpgrade.upgrade_value,
        });
      }

      router.push({pathname: `/${i18n.lc}/buy-gift-card/confirm?voucher=${voucherId}`});
    }, async (error) => {
      if (error.json && error.json.httpCode === 402) {
        try {
          // 3d secure
          const response = await this.props.billing.verify3DS({
            amount: this.orderContent.amount,
            nonce: error.json.error.nonce,
          }, error.json.gatewayName)
            .catch((err) => {
              tracker.track('3DS Failure', {
                category: 'Change booking checkout state',
                charge_id: err.chargeId || null,
              });

              notifications.add({
                id: 'gifts-checkout-state-error-payment',
                type: 'error',
                title: i18n.__error(err),
              });

              if (!i18n.isKnownError(error)) {
                // This is unexpected so record it in Sentry
                if (isValidException(error)) {
                  Sentry.captureException(error);
                } else {
                  Sentry.captureMessage('Gifts checkout complete purchase error');
                }
              }
            });

          this.processingForm = false;

          if (!response) return false;

          this.orderContent.paymentData.chargeTokenData.gateway = error.json.gatewayName;

          const newChargeTokenData = {
            ...this.orderContent.paymentData.chargeTokenData,
            extraGatewayData: response.query,
            paymentMethodNonce: response.nonce,
          };

          this.orderContent.paymentData = {
            ...this.orderContent.paymentData,
            chargeTokenData: newChargeTokenData,
            passed3DSecure: true,
          };

          // try again
          return this.completePurchase();
        } catch (_error) {
          error = _error; // eslint-disable-line no-param-reassign

          if (!i18n.isKnownError(_error)) {
            // This is unexpected so record it in Sentry
            if (isValidException(_error)) {
              Sentry.captureException(_error);
            } else {
              Sentry.captureMessage('Gifts checkout complete purchase exception');
            }
          }
        }
      } else {
        this.orderContent.paymentNonce = undefined;

        this.processingForm = false;

        this.props.notifications.add({
          id: 'gifts-purchase-error',
          type: 'error',
          title: i18n.__error(error),
        });

        const {gifts} = this.props;

        tracker.track('Error payment on gifts checkout page', {
          category: 'Gifts Checkout',
          label: error,
          city: gifts && gifts.orderContent && gifts.orderContent.city_id,
        });
      }
    });
  }

  @autobind
  @action('sessionSuccess - GiftsCheckout')
  sessionSuccess() {
    this.props.setShowFooter(true);
  }

  /*
   *  When the checkout form is submitted
   */
  @autobind
  @action('onReceivePaymentForm - GiftsCheckout')
  onReceivePaymentForm(state) {
    const {billing, notifications} = this.props;
    const {paymentInfo} = billing;
    const {i18n} = this.context;

    this.processingForm = true;

    this.orderContent.fromEmail = this.props.session.user.email;

    // If the user has selected ApplePay
    if (paymentInfo.paymentOption === PAYMENT_TYPES.APPLE_PAY && this.session) {
      return this.session.begin();
    }

    let chargeData;

    if (billing && billing.cardGateway) {
      if (paymentInfo.paymentOption === PAYMENT_TYPES.CARD) {
        chargeData = {
          gateway: billing.cardGateway.gateway,
          gatewayTokenizationType: PAYMENT_TYPES.CARD,
        };
      }

      this.orderContent.paymentData = {
        chargeTokenData: chargeData,
        clientSupports3DSecure: true,
        passed3DSecure: false,
        paymentMethod_id: paymentInfo.paymentToken,
        paymentType: paymentInfo.paymentOption,
        redirectBase: i18n.baseDomain,
      };

      this.completePurchase();
    } else {
      // This is unexpected so record it in Sentry
      Sentry.captureMessage('billing.cardGateway undefined');

      notifications.add({
        id: 'purchase-error',
        type: 'error',
        title: i18n._x('Something went wrong, please refresh the page', 'Gifts checkout page'),
      });
    }
  }

  /*
   *  Get the currency data for the current location
   */
  @computed get currencyData() {
    const {pricing} = this.props;
    if (!pricing) return null;

    const {modelState} = pricing;
    if (!modelState) return null;

    const {currency} = modelState;

    return currency || null;
  }

  /*
   *  Get the currency symbol for the current location
   */
  @computed get currencySymbol() {
    const {pricing} = this.props;
    if (!pricing) return null;

    const {modelState} = pricing;
    if (!modelState) return null;

    const {currency} = modelState;
    if (!currency) return null;

    return currency.textSymbol;
  }

  @computed get paymentSection() {
    const {
      session,
    } = this.props;
    const {
      currencySymbol,
      onApplePayClick,
      onReceivePaymentForm,
      orderContent,
      paymentInfo,
      processingForm,
    } = this;

    if (!session || (session && !session.user)) {
      return null;
    }

    return (
      <GiftsPaymentSection
        currencySymbol={currencySymbol}
        onApplePayClick={onApplePayClick}
        onReceivePaymentForm={onReceivePaymentForm}
        orderContent={orderContent}
        paymentInfo={paymentInfo}
        processingForm={processingForm}
      />
    );
  }

  @computed get loginScreen() {
    const { session } = this.props;
    if (session && session.user) {
      return null;
    }

    return (
      <GiftsRedeemSession
        disableTitle
        disableVoucherField
        startSignin
        trackerLocation="Gifts Checkout"
      />
    );
  }

  render() {
    const {
      paymentSection,
      loginScreen,
    } = this;

    return (
      <React.Fragment>
        {loginScreen}
        {paymentSection}
      </React.Fragment>
    );
  }
}

export default GiftsCheckout;



// WEBPACK FOOTER //
// ./spa/Pages/Gifts/GiftsCheckout.jsx